<script lang="ts">
	import ExternalLink from '$lib/icons/externalLink.svelte';
	import { getSbUrl } from '$lib/utils/storyblok/url';
	import { getBlogArticleSubHeading, getExternalArticleSubHeading } from '../blogHelpers';
	import {
		isExternalArticle,
		type SbBlogArticleStory,
		type SbBlogExternalArticleStory
	} from '../types';
	import Image from './image.svelte';

	interface Props {
		story: SbBlogArticleStory | SbBlogExternalArticleStory;
	}

	let { story }: Props = $props();
</script>

<a
	href={isExternalArticle(story) ? getSbUrl(story.content.link) : `/${story.full_slug}`}
	class="group"
	target={isExternalArticle(story) ? '_blank' : null}
	rel={isExternalArticle(story) ? 'noopener noreferrer' : null}
>
	<article>
		<div class="mb-3 aspect-5/4 w-full overflow-hidden rounded-xl lg:mb-5 lg:aspect-3/2">
			{#if story.content.hero_image?.filename}
				<Image
					wrapperClass="w-full"
					class="h-full w-full transform-gpu bg-stone-4 object-cover object-center transition-transform duration-600 group-hover:scale-105"
					src={story.content.hero_image.filename}
					alt={story.content.hero_image.alt ?? ''}
					title={story.content.hero_image.title ?? ''}
					width={780}
					sizes={{ 1024: { width: 2280 } }}
				/>
			{/if}
		</div>
		<div class="flex flex-col items-start gap-y-2 lg:gap-y-3">
			{#if isExternalArticle(story)}
				<div class="flex items-center gap-x-1 py-2 text-green-2 lg:text-brown-2">
					<div class="text-[14px]/[140%] font-semibold lg:text-[18px]/[140%]">
						{story.content.link_title}
					</div>
					<ExternalLink class="h-4 w-4 lg:h-6 lg:w-6" />
				</div>
				<h1 class="text-[24px]/[110%] font-semibold text-brown-2 lg:text-[32px]/[110%]">
					{story.content.title}
				</h1>
				<div class="text-[14px]/[140%] text-brown-3 lg:text-[16px]/[140%]">
					{getExternalArticleSubHeading(story)}
				</div>
			{:else}
				{#if !story.full_slug.startsWith('news/')}
					{@const category = story.content.category?.content}
					{#if category}
						<div
							class="bg-{category.list_badge_color} text-{category.list_text_color} rounded-[4px] p-2 text-[14px]/[100%] font-semibold lg:px-3 lg:py-2 lg:text-[18px]/[100%]"
						>
							{category.name}
						</div>
					{/if}
				{/if}
				<div class="text-brown-2">
					<h1 class="text-[24px]/[110%] font-semibold lg:text-[32px]/[110%]">
						{story.content.title}
					</h1>
					{#if story.content.subtitle && story.content.subtitle.length > 0}
						<h2 class="text-[18px]/[110%] lg:text-[20px]/[140%]">
							{story.content.subtitle}
						</h2>
					{/if}
				</div>
				<div class="text-[14px]/[140%] text-brown-3 lg:text-[16px]/[140%]">
					{getBlogArticleSubHeading(story)}
				</div>
			{/if}
		</div>
	</article>
</a>
